import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'

import type { BandResult } from './handleclaims'

/**
 * One customer's own orchestrator answers (`rc-peruser`), from
 * `GET /v1/admin/customers/{customerID}/orchestrator-mappings`.
 *
 * # A band on Customer 360, and NOT a screen — the route shape says so
 *
 * `rc-peruser` was designed as a list of everybody's mappings, and that shape
 * is refused on purpose. There is no collection route and there will not be:
 *
 * > *"A collection with a `?customer_id=` filter is the same read one refactor
 * > away from enumerating everybody — dropping a filter is a smaller and more
 * > plausible edit than deleting a path segment, and invariant 11b is exactly
 * > the rule that a plausible edit must not be able to break."*
 *
 * The per-customer read is legitimate and is what an operator actually needs
 * when asking why one person's credit was tagged the way it was. So it lives
 * where the customer already is. A rail screen would need a customer picker
 * over a rail that was closed deliberately, and building one would be this
 * console re-opening by the front door what the backend shut at the back.
 *
 * # Law 17, and the backend goes further than the law does
 *
 * > *User feedback never mutates the payer catalog. Feedback and per-user
 * > orchestrator mapping screens carry **no promote action at all**.*
 *
 * There is no promote here, no bulk apply, and no "make this the default". The
 * backend's own reasoning is stronger than the law's: the table has **no
 * operator write path anywhere**, because *"an operator who could set another
 * person's mapping could attribute their income by hand — off the catalog, with
 * no candidate queue and no promotion to review, which is every safeguard
 * invariant 11 puts in front of a catalog edit."*
 *
 * So this band renders no control of any kind, and the reason is not a
 * permission an operator could be granted. A correction goes through the
 * customer, or through the catalog with its own review.
 *
 * # Three states that look alike, and the operator's next move differs for each
 *
 * **`declined` is an answer, not an absence.** A customer who was asked and
 * chose not to say has a LIVE row recording it, deliberately: *"the point of
 * storing a refusal is that the prompt does not come back."* Rendered as "no
 * mapping" it reads as never-asked, and the obvious next action — ask them — is
 * the one thing the row exists to prevent.
 *
 * **Redacted** is an Article 17 erasure. The platform they named is gone; that
 * an answer was given remains, because that is not itself personal data. The
 * moment of the request is deliberately not served and so cannot be drawn.
 *
 * **Superseded** is history, kept because rows are never updated in place
 * (invariant 9). It is not a current state and must not read as one.
 *
 * # Two customers answering differently is not a discrepancy
 *
 * *"Two people paid through the same orchestrator by different platforms are
 * both answering correctly"* (invariant 11b). Nothing on this band invites a
 * comparison across customers, because there is no read that could make one.
 */

export function customerMappingsPath(id: number): string {
  return `/customers/${String(id)}/orchestrator-mappings`
}

interface Mapping {
  readonly orchestratorId: string
  readonly orchestratorName: string
  /** `platform` or `declined`. BOTH ARE ANSWERS. */
  readonly answer: string
  readonly platformId: string
  readonly platformName: string
  readonly version: number | null
  readonly effectiveFrom: string
  readonly effectiveTo: string
  readonly live: boolean | null
  readonly redacted: boolean
  readonly createdBy: string
  readonly createdAt: string
}

function readMapping(raw: unknown): Mapping | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const orchestratorId = str(o['orchestrator_id'])
  if (orchestratorId === '') return null
  return {
    orchestratorId,
    orchestratorName: str(o['orchestrator_name']),
    answer: str(o['answer']),
    platformId: str(o['platform_id']),
    platformName: str(o['platform_name']),
    version: int(o['version']),
    effectiveFrom: str(o['effective_from']),
    effectiveTo: str(o['effective_to']),
    live: typeof o['live'] === 'boolean' ? o['live'] : null,
    redacted: o['redacted'] === true,
    createdBy: str(o['created_by']),
    createdAt: str(o['created_at']),
  }
}

const COLS: readonly Column[] = [
  ['Route', 'x', 'minmax(190px,1.3fr)'],
  ['What they said', 'x', 'minmax(200px,1.4fr)'],
  ['Answer', 'c', '170px'],
  ['Version', 'm', '96px'],
  ['Answered', 'm', '120px'],
  ['Still current', 'c', '130px'],
]

/**
 * What the customer said, and the three ways there can be no platform name.
 *
 * `platform_id` is absent for a declined answer AND for a redacted one, so this
 * never decides from its emptiness — `answer` and `redacted` are the fields
 * that tell them apart, exactly as the backend spells out.
 */
function saidCell(m: Mapping): Cell {
  if (m.redacted) return 'ERASED at the customer’s request — what they named is gone'
  if (m.answer === 'declined') return 'they were asked and chose not to say'
  if (m.platformName !== '') return m.platformName
  if (m.platformId !== '') return m.platformId
  return '— an answer with no platform on it'
}

/**
 * The answer, as a state rather than as a presence.
 *
 * `declined` is deliberately not `n` and never reads as an absence: it is a
 * recorded decision, and the row exists so that nobody asks again.
 */
function answerCell(m: Mapping): ChipCell {
  if (m.redacted) return ['erased — Art. 17', 'n']
  switch (m.answer) {
    case 'platform': return ['named a platform', 'g']
    case 'declined': return ['ASKED — declined', 'a']
    case '': return ['— not stated', 'r']
    default: return [m.answer, 'n']
  }
}

function currentCell(m: Mapping): ChipCell {
  if (m.live === null) return ['— not stated', 'r']
  return m.live ? ['current', 'g'] : ['superseded', 'n']
}

function row(m: Mapping): readonly Cell[] {
  return [
    // The display name, because `via_masspay` is an internal identifier and the
    // only alternative is this console shipping its own copy of a catalog the
    // service owns.
    m.orchestratorName === '' ? m.orchestratorId : `${m.orchestratorName} · ${m.orchestratorId}`,
    saidCell(m),
    answerCell(m),
    m.version === null ? '—' : `v${String(m.version)}`,
    m.createdAt === '' ? '—' : day(m.createdAt),
    currentCell(m),
  ]
}

/**
 * The band, or the sentence that stands in for it.
 *
 * Scoped like every other band on this screen: a failure here costs the
 * mappings and nothing else, because a support agent chasing a payment should
 * not lose the balance because one endpoint was slow.
 */
export function mappingsBand(
  attempt: { readonly ok: true; readonly payload: unknown }
    | { readonly ok: false; readonly reason: string; readonly status: number | null
        readonly decisionRef: string | null; readonly refused: boolean },
): BandResult {
  if (!attempt.ok) {
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Orchestrator answers',
        title: 'What this customer said about who pays them could not be read',
        body: `${attempt.reason} Nothing below is known — which is different from a customer who was never asked, and different again from one who was asked and declined. The rest of this screen is unaffected.`,
        ref: attempt.decisionRef !== null
          ? `decision_ref ${attempt.decisionRef}`
          : attempt.refused
            ? 'The backend refused this read. It needs customer.read.'
            : 'Nothing in this band was read.',
      }],
      missing: ['what this customer said about which platform pays them'],
    }
  }

  const payload = typeof attempt.payload === 'object' && attempt.payload !== null
    ? (attempt.payload as Record<string, unknown>)
    : {}
  const raw = Array.isArray(payload['mappings']) ? (payload['mappings'] as readonly unknown[]) : []

  const rows: Mapping[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readMapping(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const declined = rows.filter((m) => m.answer === 'declined' && m.live === true).length
  const erased = rows.filter((m) => m.redacted).length

  const table: TableBlock = {
    t: 'table',
    title: 'What this customer said about who pays them',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} answer(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'This customer’s own answers, and they say nothing about anybody else’s. The same orchestrator legitimately means different platforms for different people, so there is no cross-customer view of this and no route that could produce one.',
  }

  const blocks: Block[] = [table]

  if (declined > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Asked and declined',
      title: `${String(declined)} live answer(s) here are a refusal, not a gap`,
      body: 'The customer was asked which platform pays them through that route and chose not to say, and the row exists so that the prompt does not come back. Reading it as “not yet asked” leads to exactly the action it was recorded to prevent. There is nothing to chase here.',
      ref: 'answer = declined, and the row is live',
    })
  }

  if (erased > 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Erased',
      title: `${String(erased)} answer(s) were erased at the customer’s request`,
      body: 'The platform they named is gone under their Article 17 right. That an answer was given remains, because that is not itself personal data — and when the erasure was asked for is deliberately not served, so it is not shown. There is nothing here to recover and nothing to ask them again.',
      ref: 'redacted is true on those rows',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Never asked',
      title: 'This customer has not been asked which platform pays them',
      body: 'Customers are asked only when a credit arrives on an orchestrator route, so no answer means no such credit has arrived — not that they were asked and said nothing. Those are different states and this one has no row at all; a declined answer would appear below with its own chip.',
      ref: 'no mapping rows exist for this customer',
    })
  }

  // The backend's own three sentences, carried rather than paraphrased.
  for (const [key, kicker] of [
    ['scope_note', 'This customer only'],
    ['declined_note', 'Declined is an answer'],
    ['control_note', 'Read only, and no write path exists anywhere'],
  ] as const) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone: 'n', kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable mapping row(s)`)
  if (erased > 0) missing.push(`${String(erased)} erased answer(s) — the platform named is gone`)

  return { blocks, missing }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
